import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { CircleCheck, CircleX } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { QueryKeys, STORAGE, STORAGE_KEY } from "@/constants";
import IDENTIFIERS from "@/constants/test_id";
import { AuthMode } from "@/context/auth-provider";
import { queryClient } from "@/hooks/use-query-client";
import useUserInfo from "@/hooks/use-user-info";
import { inviteUserSchema } from "@/lib/form-schema";
import { getItem } from "@/lib/storage-utils";
import { lowerCaseComparison } from "@/lib/string-util";
import type { Relation } from "@/model/demographics";
import { InputWithLabel } from "@/pages/components/input-label";
import PersonAvatar from "@/components/custom-ui/person-avatar";
import { inviteUser } from "@/services/invite-user";
import {
  LogCategories,
  LogSubCategories,
} from "@/services/logger/logger-types";
import type { InviteUserResponse } from "@/types/invite-user";
import type { DialogProps } from "../components/paperlessEob/ManageEob";
import { PRODUCT_NAME } from "@/lib/client-utils";

type InviteUserProps = DialogProps & {
  preselectedMemberId?: string;
};

const MAX_RETRY_ATTEMPTS = 1;

export function InviteUser({
  dialogOpen,
  setDialogOpen,
  logDialogName,
  logTrigger,
  preselectedMemberId,
}: InviteUserProps) {
  const userInfo = useUserInfo();

  const logCategory = Object.values(LogCategories).includes(
    logTrigger as LogCategories,
  )
    ? (logTrigger as LogCategories)
    : undefined;

  const inviteMutation = useMutation({
    mutationFn: inviteUser,
    onSuccess: (data: InviteUserResponse) => {
      if (!data.email_success) {
        console.error("Failed to invite user:", data.message);
        setFailureCount((count) => count + 1);
        setApiFailureMessage(data.message);
        return;
      }
      setFailureCount(0);
      setApiFailureMessage(undefined);
      setSuccessMessage(data.message || "Invitation sent successfully.");
    },
    onError: (error: AxiosError) => {
      console.error("Failed to invite user:", error);
      setFailureCount((count) => count + 1);
      setApiFailureMessage(undefined);
    },
  });

  const [selectedMemberId, setSelectedMemberId] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [emailError, setEmailError] = useState<string | undefined>(undefined);
  const [failureCount, setFailureCount] = useState(0);
  const [successMessage, setSuccessMessage] = useState<string | undefined>(
    undefined,
  );
  const [apiFailureMessage, setApiFailureMessage] = useState<
    string | undefined
  >(undefined);

  const dependents =
    (queryClient.getQueryData([QueryKeys.DEPENDENTS]) as Relation[]) ?? [];

  useEffect(() => {
    if (!dialogOpen) return;

    const preselectedDependent = dependents.find(
      (dependent) => dependent.memberId === preselectedMemberId,
    );

    setSelectedMemberId(preselectedDependent?.memberId ?? "");
    setEmail(preselectedDependent?.email ?? "");
    setEmailError(undefined);
    setFailureCount(0);
    setSuccessMessage(undefined);
    setApiFailureMessage(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialogOpen, preselectedMemberId]);

  const { appName } = getItem(STORAGE_KEY.PRODUCT_CONFIG, STORAGE.SESSION);
  const selectedDependent = dependents.find(
    (dependent) => dependent.memberId === selectedMemberId,
  );
  const { mode } = getItem(STORAGE_KEY.AUTH_DATA, STORAGE.SESSION);
  const isGuestMode = lowerCaseComparison(mode, AuthMode.GUEST);
  const canRetry = failureCount > 0 && failureCount <= MAX_RETRY_ATTEMPTS;
  const retriesExhausted = failureCount > MAX_RETRY_ATTEMPTS;
  const showResult = !!successMessage || failureCount > 0;
  const canSubmit =
    !!selectedDependent && !!email && !emailError && !inviteMutation.isPending;
  const isSuccess = !!successMessage;
  const resultMessage = showResult
    ? {
        title: isSuccess ? "Invitation Sent" : "Invitation Failed",
        body: isSuccess
          ? successMessage
          : retriesExhausted
            ? "We're still unable to send this invitation. Please try again later or contact support."
            : apiFailureMessage ||
              "Something went wrong while sending the invitation.",
        confirmBtnLabel: "OK",
        icon: isSuccess ? CircleCheck : CircleX,
        iconClassName: isSuccess
          ? "text-blue-400 bg-blue-100"
          : "text-red-700 bg-red-100",
        ...(!isSuccess && canRetry
          ? { secondaryBtnLabel: "Try Again", onSecondaryClick: handleRetry }
          : {}),
      }
    : undefined;

  function handleDependentChange(memberId: string) {
    setSelectedMemberId(memberId);
    const selected = dependents.find(
      (dependent) => dependent.memberId === memberId,
    );
    setEmail(selected?.email ?? "");
    setEmailError(undefined);
  }

  function handleEmailChange(value: string) {
    setEmail(value);
    if (emailError) setEmailError(undefined);
  }

  function handleEmailBlur() {
    const result = inviteUserSchema.shape.email.safeParse(email);
    setEmailError(result.success ? undefined : result.error.errors[0].message);
  }

  function handleClose(open: boolean) {
    if (!open) {
      inviteMutation.reset();
    }
    setDialogOpen(open);
  }

  function handleSubmit(event?: React.FormEvent<HTMLFormElement>) {
    event?.preventDefault();
    const result = inviteUserSchema.shape.email.safeParse(email);
    if (!result.success) {
      setEmailError(result.error.errors[0].message);
      return;
    }
    if (!selectedDependent) return;

    inviteMutation.mutate({
      member: selectedDependent.memberId,
      employee: selectedDependent.employeeId,
      group: selectedDependent.groupId,
      firstname: selectedDependent.firstName,
      lastname: selectedDependent.lastName,
      email,
      loginGroupId: userInfo.loginGroupId,
    });
  }

  function handleAcknowledgeResult() {
    inviteMutation.reset();
    setFailureCount(0);
    setSuccessMessage(undefined);
    setApiFailureMessage(undefined);
    setSelectedMemberId("");
    setEmail("");
    setDialogOpen(false);
  }

  function handleRetry() {
    inviteMutation.reset();
    handleSubmit();
  }

  return (
    <Dialog
      open={dialogOpen}
      onOpenChange={(open) => {
        if (!open && showResult) {
          handleAcknowledgeResult();
          return;
        }
        handleClose(open);
      }}
      logDialogName={logDialogName}
      logTrigger={logTrigger}
      logCategory={logCategory}
      logSubCategory={LogSubCategories.INVITE_USER}
    >
      <DialogContent className="sm:max-w-[500px]" logDialogName={logDialogName}>
        {/* 4.1.2 Name, Role, Value — A: the dialog had no accessible name, so a screen
            reader announced only "dialog" when it opened. */}
        <DialogTitle className="sr-only">Invite a user</DialogTitle>
        {resultMessage ? (
          <div className="flex min-h-[200px] flex-col items-center justify-between gap-2 p-2">
            {resultMessage.icon && (
              <div
                className={`flex w-fit justify-center rounded-full p-3 ${resultMessage.iconClassName ?? ""}`}
              >
                <resultMessage.icon className="size-10" strokeWidth={1.2} />
              </div>
            )}
            <div className="text-center">
              <p className="text-lg font-medium">{resultMessage.title}</p>
            </div>
            <p className="flex w-full items-center justify-center text-center text-sm text-muted-foreground">
              {resultMessage.body}
            </p>
            <div className="mt-2 flex w-full items-center justify-center gap-3">
              {resultMessage.secondaryBtnLabel &&
                resultMessage.onSecondaryClick && (
                  <Button
                    variant="outline"
                    className="min-w-28 outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    data-testid={IDENTIFIERS.INVITE_USER.TRY_AGAIN_BUTTON}
                    onClick={resultMessage.onSecondaryClick}
                  >
                    {resultMessage.secondaryBtnLabel}
                  </Button>
                )}
              <Button
                variant="default"
                className="min-w-28 outline-none hover:bg-primary-dark focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                onClick={handleAcknowledgeResult}
                data-testid={IDENTIFIERS.INVITE_USER.ACKNOWLEDGE_BUTTON}
              >
                {resultMessage.confirmBtnLabel}
              </Button>
            </div>
          </div>
        ) : (
          <div defaultValue="notifications" className="rounded-2xl bg-white">
            <div className="mx-auto max-w-4xl">
              <div className="add-class">
                <Card className="border-none shadow-none">
                  <CardHeader className="border-b border-gray-300 p-2 pb-6">
                    <CardTitle as="h2">
                      Invite User to {appName || PRODUCT_NAME.ZCONNECT}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="mt-6 grid w-full gap-y-4 p-0">
                    <form
                      className="flex max-h-[500px] flex-col gap-4 overflow-y-auto p-1"
                      onSubmit={handleSubmit}
                    >
                      <p className="text-sm text-neutral-500">
                        You can invite family members or other people to{" "}
                        {appName || PRODUCT_NAME.ZCONNECT} via Email.
                      </p>
                      <div className="grid w-full items-center gap-3">
                        <Label htmlFor="dependent">Select a dependent</Label>
                        <Select
                          value={selectedMemberId}
                          onValueChange={handleDependentChange}
                          disabled={!!preselectedMemberId}
                        >
                          <SelectTrigger
                            id="dependent"
                            className="h-11 border-0 bg-gray-100 text-sm focus:ring-2 focus:ring-gray-200 focus:ring-offset-2"
                          >
                            <SelectValue placeholder="Select a dependent to invite" />
                          </SelectTrigger>
                          <SelectContent className="mt-1">
                            {dependents.map(
                              (dependent) =>
                                !dependent?.username && (
                                  <SelectItem
                                    key={dependent.memberId}
                                    value={dependent.memberId}
                                    className="cursor-pointer py-2.5 hover:bg-primary-extraLight"
                                  >
                                    <span className="flex items-center gap-2">
                                      <PersonAvatar
                                        className="size-6"
                                        firstName={dependent.firstName}
                                        lastName={dependent.lastName}
                                      />
                                      {dependent.firstName} {dependent.lastName}
                                    </span>
                                  </SelectItem>
                                ),
                            )}
                          </SelectContent>
                        </Select>
                      </div>

                      <InputWithLabel
                        id="email"
                        label="Send To"
                        placeholder="Please enter email"
                        className="text-sm"
                        value={email}
                        onChange={handleEmailChange}
                        onBlur={handleEmailBlur}
                        error={emailError}
                        data-testid="invite-user-email"
                      />

                      <div className="flex justify-end gap-3 pt-2">
                        <DialogClose asChild logDialogName={logDialogName}>
                          <Button
                            variant="outline"
                            className="min-w-28"
                            data-testid={IDENTIFIERS.INVITE_USER.CANCEL_BUTTON}
                          >
                            Cancel
                          </Button>
                        </DialogClose>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span
                              tabIndex={isGuestMode ? 0 : undefined}
                              className="inline-flex"
                            >
                              <Button
                                type="submit"
                                className="min-w-28 disabled:pointer-events-none"
                                data-testid={
                                  IDENTIFIERS.INVITE_USER.INVITE_BUTTON
                                }
                                logMetadata={{
                                  dependentEmail: selectedDependent?.email,
                                  dependentMemberId:
                                    selectedDependent?.memberId,
                                  dependentName: `${selectedDependent?.firstName} ${selectedDependent?.lastName}`,
                                }}
                                disabled={!canSubmit || isGuestMode}
                              >
                                {inviteMutation.isPending
                                  ? "Inviting ..."
                                  : "Invite"}
                              </Button>
                            </span>
                          </TooltipTrigger>
                          {isGuestMode && (
                            <TooltipContent className="z-[200]">
                              <p>Invite User disabled on Guest Mode</p>
                            </TooltipContent>
                          )}
                        </Tooltip>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
